import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/contexts/ContextoWorkspace';
import { esEmpresaDemo } from '@/shared/demo';
import { estaHabilitadaPseMock, suscribirseAHabilitacionPseMock } from '@/shared/habilitacionPseMock';
import { usePanelFlotanteAbierto } from '@/shared/overlay/estadoPanelesFlotantes';
import { OnboardingChecklist } from './OnboardingChecklist';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';
import { useDelayedFalse } from '../hooks/useDelayedFalse';
import { useHasOpenModal } from '../store/useModalTrackerStore';
import { HIDE_ON_ROUTES, resolveStepRoute } from '../config/onboarding.config';

interface ChecklistInicioMountProps {
  /** Modales locales del propio Layout (logout, atajos, paleta, bienvenida). */
  isModalOpen?: boolean;
  /** Aviso breve tras ocultar el checklist. Conectado al sistema de toasts del proyecto. */
  onNotify?: (message: string) => void;
}

/**
 * Punto de montaje real del checklist dentro de PrivateLayout. Traduce el
 * contexto de workspace del proyecto al contrato genérico de
 * OnboardingChecklist, y combina los modales que Layout ya conoce con el
 * registro global de useModalTrackerStore. El retorno se demora 240ms para que
 * el lanzador no reaparezca de golpe apenas un modal empieza a cerrarse.
 *
 * Senciyo SAC (RUC demo) se crea vacía en cada cuenta nueva y suele quedar
 * como esPrincipal por ser la primera fila insertada, pero no es una empresa
 * real del usuario: el checklist no debe mostrarse ahí, y "primera empresa"
 * se decide entre las empresas reales (por fecha de creación), no por ese
 * flag del backend.
 *
 * No se muestra:
 * - en la empresa demo (Senciyo SAC), ni hasta que la empresa esté habilitada
 *   por PSE ante SUNAT (sin eso no puede emitir comprobantes electrónicos
 *   válidos). Se reutiliza la misma fuente que HabilitacionPseBanner
 *   (`@/shared/habilitacionPseMock`);
 * - en las rutas de emisión y POS (HIDE_ON_ROUTES): ahí actúan los
 *   recordatorios dentro del formulario (OnboardingFieldHint);
 * - mientras haya un modal o drawer registrado abierto.
 *
 * Las reglas por empresa (2 en adelante, inactividad, oculto, retirado) las
 * resuelve OnboardingChecklist con el store.
 */
export function ChecklistInicioMount({ isModalOpen = false, onNotify }: ChecklistInicioMountProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { empresaId, currentEmpresa, empresas } = useWorkspace();

  const [, forzarRefrescoHabilitacionPse] = useState(0);
  useEffect(
    () => suscribirseAHabilitacionPseMock(() => forzarRefrescoHabilitacionPse((n) => n + 1)),
    [],
  );

  const isDemoCompany = esEmpresaDemo(currentEmpresa?.ruc);
  const isPseHabilitada = !!currentEmpresa && estaHabilitadaPseMock(currentEmpresa.ruc);
  useOnboardingProgress(isDemoCompany ? null : empresaId);

  const hasTrackedModalOpen = useHasOpenModal();
  // Los Drawer (crear cliente, etc.) ya se registran aquí; el checklist se retira con ellos.
  const hasFloatingPanelOpen = usePanelFlotanteAbierto();
  const delayedModalOpen = useDelayedFalse(isModalOpen || hasTrackedModalOpen || hasFloatingPanelOpen, 240);
  const isEmissionRoute = HIDE_ON_ROUTES.some((route) => pathname.startsWith(route));

  if (!empresaId || !currentEmpresa || isDemoCompany || !isPseHabilitada) return null;

  const firstRealCompanyId = [...empresas]
    .filter((e) => !esEmpresaDemo(e.ruc))
    .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''))[0]?.id;

  return (
    <OnboardingChecklist
      company={{
        id: empresaId,
        name: currentEmpresa.razonSocial,
        ruc: currentEmpresa.ruc,
        isFirstCompany: empresaId === firstRealCompanyId,
      }}
      onNavigate={(route) => navigate(resolveStepRoute(route, empresaId))}
      onIssueInvoice={() => navigate('/comprobantes/emision')}
      isModalOpen={delayedModalOpen}
      isSuppressed={isEmissionRoute}
      onHidden={onNotify}
    />
  );
}
